import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { CircularProgress, makeStyles } from '@material-ui/core'
import RoundedModal from '../../../../molecules/RoundedModal'
import SydButton from '../../../../commonDesign/Button'
import SydLabel, { hookFormFieldErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import { useGroupSearch } from '../../../../../api/groups'
import {
  useAssignRegistrationOwnerGroupMutation,
  useModifyRegistrationOwnerWeightMutation,
  useRemoveRegistrationOwnerAssignmentMutation
} from '../../../../../api/householdRegistrations'
import CreateOwnerGroupModal from './CreateOwnerGroupModal'

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '0 4px'
  },
  registrationLine: {
    fontSize: '0.875rem',
    color: 'var(--color-text-secondary)',
    paddingBottom: '4px',
    borderBottom: '1px solid var(--color-neutral-300)'
  },
  warning: {
    background: 'var(--color-surface-warning-subtle, #fff8e1)',
    border: '1px solid var(--color-border-warning, #ffe082)',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem'
  },
  staleBanner: {
    background: 'var(--color-surface-error-subtle, #ffebee)',
    border: '1px solid var(--color-border-error, #ef9a9a)',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    color: 'var(--color-text-error, #b71c1c)'
  },
  createLink: {
    fontSize: '0.85rem',
    cursor: 'pointer',
    color: 'var(--color-primary-500)',
    textDecoration: 'underline',
    background: 'transparent',
    border: 'none',
    padding: 0,
    alignSelf: 'flex-start'
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '8px'
  },
  actionsRight: {
    display: 'flex',
    gap: '8px'
  },
  groupFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  groupFieldFlex: {
    flex: 1
  }
}))

/**
 * Edit / assign an owner-group + weight for a registration.
 *
 * mode='edit'   — registration already has the assignment; weight is editable;
 *                 owner group is fixed; "Remove" button visible.
 * mode='assign' — no existing assignment for this (registration, owner-group);
 *                 admin picks the owner group and weight.
 *
 * Also offers an inline "+ Create new owner group" link that opens
 * CreateOwnerGroupModal; on success the new group is auto-selected.
 *
 * Stale-row handling (GAP A): if the server returns 409 ("Row was modified
 * or removed by another user"), we show an inline error and ask the admin
 * to close the modal — refetch happens via the React Query invalidation.
 */
function EditAssignmentModal ({ open, onClose, mode, clientId, row }) {
  const classes = useStyles()

  const groupSearchQuery = useMemo(() => ({
    filters: { levelTypeId: [{ op: 'eq', value: 207 }] },
    take: 200
  }), [])
  const { data: groupSearch, isLoading: groupsLoading } = useGroupSearch(groupSearchQuery, { enabled: open })
  const groupOptions = useMemo(() => (
    (groupSearch?.data || []).map((g) => ({ value: g.groupId, label: g.longName || g.shortName || `Group ${g.groupId}` }))
  ), [groupSearch])

  const { mutateAsync: assignMut } = useAssignRegistrationOwnerGroupMutation()
  const { mutateAsync: modifyMut } = useModifyRegistrationOwnerWeightMutation()
  const { mutateAsync: removeMut } = useRemoveRegistrationOwnerAssignmentMutation()

  const { handleSubmit, control, formState, reset, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      groupId: null,
      weight: 1
    }
  })

  useEffect(() => {
    if (open) {
      reset({
        groupId: mode === 'edit' ? row?.ownerGroupId ?? null : null,
        weight: mode === 'edit' && row?.weight != null ? Number(row.weight) : 1
      })
    }
  }, [open, row, mode, reset])

  const [processing, setProcessing] = useState(false)
  const [staleError, setStaleError] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const watchedWeight = watch('weight')
  const weightOutOfRange = useMemo(() => {
    const n = Number(watchedWeight)
    return Number.isFinite(n) && (n < 0 || n > 1)
  }, [watchedWeight])

  const closeAndReset = useCallback(() => {
    setProcessing(false)
    setStaleError(false)
    setCreateOpen(false)
    onClose()
  }, [onClose])

  const handleStale = useCallback((err) => {
    const status = err?.response?.data?.statusCode || err?.response?.status
    if (status === 409) {
      setStaleError(true)
      return true
    }
    return false
  }, [])

  const onSubmit = useCallback(async (form) => {
    setProcessing(true)
    setStaleError(false)
    try {
      if (mode === 'edit') {
        await modifyMut({
          clientId,
          orionRegistrationId: row.orionRegistrationId,
          groupId: row.ownerGroupId,
          weight: Number(form.weight)
        })
      } else {
        await assignMut({
          clientId,
          orionRegistrationId: row.orionRegistrationId,
          groupId: Number(form.groupId),
          weight: Number(form.weight)
        })
      }
      closeAndReset()
    } catch (err) {
      if (!handleStale(err)) {
        alert(`There was a problem ${mode === 'edit' ? 'updating' : 'assigning'} the owner group`)
        console.error(err)
      }
    } finally {
      setProcessing(false)
    }
  }, [mode, modifyMut, assignMut, clientId, row, handleStale, closeAndReset])

  const onRemove = useCallback(async () => {
    if (!window.confirm('Remove this owner-group assignment from the registration?')) return
    setProcessing(true)
    setStaleError(false)
    try {
      await removeMut({
        clientId,
        orionRegistrationId: row.orionRegistrationId,
        groupId: row.ownerGroupId
      })
      closeAndReset()
    } catch (err) {
      if (!handleStale(err)) {
        alert('There was a problem removing the owner-group assignment')
        console.error(err)
      }
    } finally {
      setProcessing(false)
    }
  }, [removeMut, clientId, row, handleStale, closeAndReset])

  const handleGroupCreated = useCallback((created) => {
    setValue('groupId', created.groupId, { shouldValidate: true, shouldDirty: true })
    setCreateOpen(false)
  }, [setValue])

  const title = mode === 'edit' ? 'Edit Ownership Weight' : 'Assign Owner Group'
  const registrationLabel = row
    ? `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.registrationLongName || `Registration ${row.orionRegistrationId}`
    : ''

  return (
    <>
      <RoundedModal
        title={title}
        open={open}
        onClose={closeAndReset}
        size='xmedium'
        position='top'
        disablePortal={false}
      >
        <div className={classes.form}>
          {row ? (
            <div className={classes.registrationLine}>
              <strong>{registrationLabel}</strong> &middot; Orion Registration {row.orionRegistrationId} &middot; Account {row.accountName || row.accountNumber}
            </div>
          ) : null}

          {staleError ? (
            <div className={classes.staleBanner}>
              This row was modified or removed by another user. Close this modal and try again — the table will refresh.
            </div>
          ) : null}

          {mode === 'assign' ? (
            <Controller
              control={control}
              name='groupId'
              rules={{ required: { value: true, message: 'Owner group is required' } }}
              render={({ field, fieldState }) => (
                <SydLabel
                  label='Owner Group'
                  error={hookFormFieldErrorAdapter(fieldState)}
                >
                  <div className={classes.groupFieldRow}>
                    <div className={classes.groupFieldFlex}>
                      <SydAutocomplete
                        options={groupOptions}
                        value={groupOptions.find(o => o.value === field.value) ?? null}
                        onChange={(e, option) => field.onChange(option?.value ?? null)}
                        placeholder='Select an owner group'
                        disabled={processing}
                      />
                    </div>
                    {groupsLoading ? <CircularProgress size={16} /> : null}
                  </div>
                </SydLabel>
              )}
            />
          ) : (
            <SydLabel label='Owner Group'>
              <div>{row?.ownerGroupName || `Group ${row?.ownerGroupId}`}</div>
            </SydLabel>
          )}

          {mode === 'assign' ? (
            <button type='button' className={classes.createLink} onClick={() => setCreateOpen(true)}>
              + Create new owner group
            </button>
          ) : null}

          <Controller
            control={control}
            name='weight'
            rules={{
              required: { value: true, message: 'Weight is required' },
              validate: (v) => {
                const n = Number(v)
                if (!Number.isFinite(n)) return 'Weight must be a number'
                if (n < 0) return 'Weight cannot be negative'
                return true
              }
            }}
            render={({ field, fieldState }) => (
              <SydLabel
                label='Weight'
                description='Decimal value where 1.00 represents 100% ownership.'
                error={hookFormFieldErrorAdapter(fieldState)}
              >
                <SydInput
                  type='number'
                  step='0.01'
                  {...field}
                  disabled={processing}
                />
              </SydLabel>
            )}
          />

          {weightOutOfRange ? (
            <div className={classes.warning}>
              Weight is outside the typical [0, 1] range. The value will still be saved.
            </div>
          ) : null}

          <div className={classes.actions}>
            <div>
              {mode === 'edit' ? (
                <SydButton variant='outline' priority='warning' onClick={onRemove} disabled={processing}>
                  Remove
                </SydButton>
              ) : null}
            </div>
            <div className={classes.actionsRight}>
              <SydButton variant='ghost' onClick={closeAndReset} disabled={processing}>Cancel</SydButton>
              <SydButton
                variant='primary'
                onClick={handleSubmit(onSubmit)}
                disabled={!formState.isValid || processing}
                processing={processing}
              >
                {mode === 'edit' ? 'Save weight' : 'Assign'}
              </SydButton>
            </div>
          </div>
        </div>
      </RoundedModal>

      <CreateOwnerGroupModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clientId={clientId}
        onCreated={handleGroupCreated}
      />
    </>
  )
}

EditAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['assign', 'edit']).isRequired,
  clientId: PropTypes.number.isRequired,
  row: PropTypes.shape({
    orionRegistrationId: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    registrationLongName: PropTypes.string,
    accountName: PropTypes.string,
    accountNumber: PropTypes.string,
    ownerGroupId: PropTypes.number,
    ownerGroupName: PropTypes.string,
    weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
}

export default EditAssignmentModal
